/**
 * What to tell the pilot about position reports reaching FlyVAC (2026-10-18).
 * The desktop app has always known when delivery stopped (offline, signed
 * out, flight gone) and sent it to the window, but nothing showed it: nothing
 * is lost, since reports wait on this computer, but a pilot could not tell
 * that nothing was being recorded on FlyVAC.
 *
 * `status` is telemetry-poster.js's { state, detail, pending }; `badSinceMs`
 * when it last stopped being ok. A dropped send or two is normal, so offline
 * and retrying are said only once they have lasted GRACE_MS; the others at once.
 * Returns { tone: "warn" | "error", title, text } or null.
 */
export const GRACE_MS = 15_000;

const waiting = (n) => (n > 0 ? ` ${n.toLocaleString()} ${n === 1 ? "report is" : "reports are"} saved on this computer.` : "");

export function telemetryNotice(status, badSinceMs, nowMs = Date.now()) {
  if (!status || !status.state) return null;
  const n = Number(status.pending) || 0;
  const lasted = badSinceMs != null && nowMs - badSinceMs >= GRACE_MS;
  switch (status.state) {
    case "offline":
      return lasted ? { tone: "warn", title: "Can't reach FlyVAC",
        text: `Position reports will be sent when the connection is back.${waiting(n)}` } : null;
    case "retrying":
      return lasted ? { tone: "warn", title: "FlyVAC isn't accepting position reports",
        text: `Trying again every few seconds.${waiting(n)}` } : null;
    case "auth_failed":
      return { tone: "error", title: "Signed out: position reports are not being sent",
        text: `Sign in again to carry on; nothing is lost.${waiting(n)}` };
    case "stopped":
      return { tone: "error", title: "This flight is no longer active on FlyVAC",
        text: "It was filed or cancelled elsewhere, so nothing more is being recorded for it." };
    case "error":
      return { tone: "error", title: "Can't save position reports on this computer",
        text: status.detail ? String(status.detail) : "The disk may be full." };
    default:
      return null;   // ok, filed
  }
}
